<template>
  <div class="wg-keyword-insight-single">
    <div v-if="bootLoading" />

    <div v-else-if="report">
      <nav class="wg-order-flow__breadcrumb-row" aria-label="Breadcrumb" style="margin-top: 20px;">
        <router-link
          :to="{ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } }"
          class="wg-order-flow__breadcrumb-link"
        >
          {{ site.name || 'Site' }}
        </router-link>
        <span class="wg-order-flow__breadcrumb-separator" aria-hidden="true">&gt;</span>
        <router-link
          :to="{ name: 'OrderContentOnlyWhiteGlove', params: { siteId: String(siteId) } }"
          class="wg-order-flow__breadcrumb-link"
        >
          Order content only
        </router-link>
        <span class="wg-order-flow__breadcrumb-separator" aria-hidden="true">&gt;</span>
        <span class="wg-order-flow__breadcrumb-current">Keyword research results</span>
      </nav>

      <div
        v-if="currentAsyncTask?.is_pending_or_in_progress"
        class="d-flex flex-column align-center pt-10 pb-8"
      >

        <div class="d-flex flex-column align-center" style="max-width: 500px; text-align: center;">
          <img src="/static/keyword-insight-processing-image.svg" width="315" height="222" class="mb-4" alt="">
          <div class="mb-6" style="font-size: 1.2rem; font-weight: bold;">Getting Keyword Insight data...</div>

          <v-progress-linear
            class="mb-4"
            style="max-width: 300px;"
            rounded
            height="5"
            color="profileAnchorColor"
            :value="currentAsyncTask.task_status.value || 0"
          />

          <div style="font-size: 1rem; color: var(--v-gray50Color-base);">{{ currentAsyncTask.task_status.status === 'PENDING' ? 'Starting...' : currentAsyncTask.task_status.msg }}</div>
        </div>
      </div>

      <template v-else>

        <div class="mb-2" style="color: var(--v-gray50Color-base); font-size: 1rem;">{{ site.name }}</div>

        <div class="wg-keyword-insight-single__header-block mb-4">
          <h1 class="wg-keyword-insight-single__title">
            Keyword research results
          </h1>
          <p class="wg-keyword-insight-single__subtitle">
            Review your keywords below. Hit Cluster with AI to auto-label them, or click any tag to set top-level and supporting yourself.
          </p>

          <div class="wg-keyword-insight-single__score-stack">
            <pop-ta-score-label
              v-if="isWhiteGloveUser && site?.ta_score"
              :ta_score="site.ta_score"
            />
            <div class="wg-keyword-insight-single__legend" aria-label="Keyword types legend">
              <span class="wg-keyword-insight-single__legend-title">Keyword types:</span>
              <span class="wg-keyword-insight-single__legend-item">
                <span class="wg-keyword-insight-single__tag wg-keyword-insight-single__tag--tl">TL</span> Top-level
              </span>
              <span class="wg-keyword-insight-single__legend-item">
                <span class="wg-keyword-insight-single__tag wg-keyword-insight-single__tag--stl">STL</span> Secondary top-level
              </span>
              <span class="wg-keyword-insight-single__legend-item">
                <span class="wg-keyword-insight-single__tag wg-keyword-insight-single__tag--s">S</span> Supporting
              </span>
              <span class="wg-keyword-insight-single__legend-hint">Click the type tag on any keyword to change it</span>
            </div>
          </div>
        </div>

        <keyword-research-result
          title="Keyword Insight"
          report-type="KeywordResearch"
          :report="report"
          :research-history="[]"
          :has-unlimited-access="hasUnlimitedAccess"
          :suppress-pop-ta-score-header="true"
          :isContentOnlyView="true"
          @refreshReport="() => loadReport()"
          @onAIResultReady="() => loadReport()"
          @onKeywordLabelUpdateSuccess="onKeywordLabelUpdateSuccess"
        />

      </template>

    </div>

    <div v-else class="card-big-title mb-8 d-flex gap10">
      No report found
    </div>

    <async-tasks-popup
      v-if="Number.isFinite(siteId)"
      :site-id="siteId"
      :campaign-id="domainLevelCampaign ? domainLevelCampaign.id : 0"
    />
  </div>
</template>

<script>
import AsyncTasksPopup from '@/components/AsyncTasksPopup.vue'
import KeywordResearchResult from '@/components/Campaigns/KeywordResearchResult.vue'
import PopTaScoreLabel from '@/components/Campaigns/PopTaScoreLabel.vue'
import {
  SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS,
  whiteGlovePlans,
} from '@/components/plans_constants'
import { getPureServicePkgName } from '@/utils'

export default {
  name: 'ContentOnlyKeywordInsightSingleWhiteGlove',
  components: {
    AsyncTasksPopup,
    KeywordResearchResult,
    PopTaScoreLabel,
  },
  data () {
    return {
      bootLoading: true,
      report: null,
      campaigns: [],
    }
  },
  computed: {
    siteId () {
      return parseInt(this.$route.params.siteId, 10)
    },
    site () {
      return this.$store.getters.siteById(this.siteId) || {}
    },
    currentUser () {
      return this.$store.state.user
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isSpecialAdminUser () {
      return this.$store.getters.isSpecialAdminUser
    },
    isSpecialOwnerSelected () {
      return this.$store.getters.isSpecialOwnerSelected
    },
    domainLevelCampaigns () {
      return this.campaigns.filter((item) => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      return this.domainLevelCampaigns.length > 0 ? this.domainLevelCampaigns[0] : null
    },
    hasUnlimitedAccess () {
      if (this.selectedSubAccountRelation) {
        if (this.isSpecialOwnerSelected) return true
        if (SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map((x) => getPureServicePkgName(x)).includes(getPureServicePkgName(this.selectedSubAccountRelation.ownerAccountType))) {
          return true
        }
      } else {
        if (this.isSpecialAdminUser) return true
        if (this.currentUser && SUBSCRIPTIONS_WITH_UNLIMITED_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map((x) => getPureServicePkgName(x)).includes(getPureServicePkgName(this.currentUser.accountType))) {
          return true
        }
      }
      return false
    },
    isWhiteGloveUser () {
      if (!this.currentUser) return false
      const accountType = this.selectedSubAccountRelation
        ? this.selectedSubAccountRelation.ownerAccountType
        : this.currentUser.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    asyncTasks () {
      return this.$store.state.asyncTasks
    },
    currentAsyncTask () {
      return this.asyncTasks.filter((task) => task.type.startsWith('KeywordResearch.') && task.record_id === this.report?.id)[0]
    },
  },
  watch: {
    currentAsyncTask (newVal, oldVal) {
      if (newVal?.is_completed && oldVal && newVal?.is_completed !== oldVal?.is_completed) {
        this.reloadUser()
        this.loadReport()
      }
    },
  },
  mounted () {
    this.$store.dispatch('loadSite', this.siteId)
      .then((response) => {
        if (response.data?.status === 'SUCCESS' && response.data.site) {
          this.$store.commit('setSite', response.data.site)
        }
      })
      .catch(() => {})
      .then(() => this.$store.dispatch('getCampaignsForSite', this.siteId))
      .then((response) => {
        if (response.data?.status === 'SUCCESS') {
          this.campaigns = response.data.campaigns || []
        }
      })
      .then(() => this.loadReport())
      .finally(() => {
        this.bootLoading = false
      })
  },
  methods: {
    reloadUser () {
      return this.$store.dispatch('loadUser').then((response) => {
        this.$store.commit('setUser', response.data.user)
      })
    },
    redirectToList () {
      this.$router.replace({
        name: 'OrderContentOnlyWhiteGlove',
        params: { siteId: String(this.siteId) },
      })
    },
    loadReport () {
      const researchId = this.$route.params.id
      return this.$store.dispatch('loadKeywordResearchData', {
        researchId,
      })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            const r = response.data.research
            const dc = this.domainLevelCampaign
            if (!r || !dc || Number(r.campaignId) !== Number(dc.id)) {
              this.report = null
              this.redirectToList()
              return
            }
            this.report = r
          } else {
            this.report = null
            this.redirectToList()
          }
        })
        .catch(() => {
          this.report = null
          this.redirectToList()
        })
    },
    onKeywordLabelUpdateSuccess (keywords, label, resetExistingTopLevelKeywords) {
      this.report = {
        ...this.report,
        result: this.report.result.map((row) => {
          if (label === 'T' && row.isTopLevelKeyword && resetExistingTopLevelKeywords) {
            return {
              ...row,
              isTopLevelKeyword: false,
              isSecondaryKeyword: false,
            }
          }
          if (keywords.includes(row.keyword)) {
            return {
              ...row,
              isTopLevelKeyword: label === 'T' || label === 'ST',
              isSecondaryKeyword: label === 'ST',
              isSupportingKeyword: label === 'S',
            }
          }
          return row
        }),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-order-flow__breadcrumb-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  color: var(--v-gray50Color-base);
  font-size: 0.9rem;
}

.wg-order-flow__breadcrumb-link {
  display: inline-flex;
  align-items: center;
  color: var(--v-mainColor-base);
  font-weight: 500;
  text-decoration: none;
}

.wg-order-flow__breadcrumb-separator {
  color: var(--v-gray20Color-base, #bdbdbd);
  margin: 0 -2px;
}

.wg-order-flow__breadcrumb-current {
  color: var(--v-gray50Color-base);
  font-weight: 500;
}

.wg-keyword-insight-single {
  padding: 8px 20px 40px;
}

.wg-keyword-insight-single__title {
  margin: 0 0 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.3;
}

.wg-keyword-insight-single__subtitle {
  margin: 0 0 16px;
  font-size: 0.95rem;
  color: var(--v-gray2Color-base, #757575);
  line-height: 1.5;
  max-width: 48rem;
}

.wg-keyword-insight-single__score-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.wg-keyword-insight-single__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  padding: 10px 20px;
  border-radius: 999px;
  font-size: 0.8rem;
  border: 1px solid var(--v-gray12Color-base);
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
}

.wg-keyword-insight-single__legend-title {
  font-weight: 700;
  margin-right: 4px;
}

.wg-keyword-insight-single__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wg-keyword-insight-single__legend-hint {
  margin-left: auto;
  color: var(--v-gray2Color-base, #757575);
  font-size: 0.78rem;
}

@media (max-width: 768px) {
  .wg-keyword-insight-single__legend-hint {
    margin-left: 0;
    flex-basis: 100%;
  }
}

.wg-keyword-insight-single__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
}

.wg-keyword-insight-single__tag--tl {
  background: #e8f5e9;
  color: #2e7d32;
}

.wg-keyword-insight-single__tag--stl {
  background: #99999933;
  color: #6A6A6A;
}

.wg-keyword-insight-single__tag--s {
  background: #fce4ec;
  color: #c2185b;
}
</style>
